import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, message, Modal, Tag } from "antd";
import { Bell, Download, Paperclip } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { api } from "@/core/api/client";
import { useFormatDateTime, useT } from "@/core/i18n";
import { usePreferences } from "@/core/state/preferences";
import { useSession } from "@/core/state/session";
import { noticeTitle, type SecurityNotice } from "@/domain/notices";
import { RichTextView } from "@/shared/ui/rich-text/RichTextView";
import { TopbarPopover } from "./TopbarPopover";

type Announcement = {
  id: string;
  title: string;
  body_html: string;
  severity: "info" | "warning" | "critical";
  read: boolean;
  published_at?: string;
};

type AnnouncementAttachment = { id: string; file_name: string; content_type: string; url: string; download_url: string };

export function NoticeButton() {
  const t = useT();
  const locale = usePreferences((state) => state.locale);
  const navigate = useNavigate();
  const formatDateTime = useFormatDateTime();
  const queryClient = useQueryClient();
  const [toast, toastContextHolder] = message.useMessage();
  const tenant = useSession((state) => state.identity?.tenantSlug) ?? "";
  const userId = useSession((state) => state.identity?.userId) ?? "";
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState<Set<string>>(() => new Set());
  const [readingAnnouncement, setReadingAnnouncement] = useState(false);
  const query = useQuery({
    queryKey: ["notices", tenant],
    queryFn: () => api.account(tenant, "/notices") as Promise<{ items: SecurityNotice[]; unread_count: number }>,
    enabled: Boolean(tenant),
    retry: false,
  });
  const announcementsQuery = useQuery({
    queryKey: ["announcements", tenant, userId, "inbox", 100],
    queryFn: () => api.announcements(tenant, "?page_size=100") as Promise<{ items: Announcement[] }>,
    enabled: Boolean(tenant && userId),
    retry: false,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
  const items = query.data?.items ?? [];
  const announcements = announcementsQuery.data?.items ?? [];
  const unreadAnnouncements = announcements.filter((item) => !item.read);
  const unread = (query.data?.unread_count ?? 0) + unreadAnnouncements.length;
  const promptAnnouncement = unreadAnnouncements.find((item) => !dismissedAnnouncements.has(`${userId}:${item.id}`));
  const promptAttachments = useQuery({
    queryKey: ["announcement-attachments", tenant, promptAnnouncement?.id],
    queryFn: () => api.announcements(tenant, `/${encodeURIComponent(promptAnnouncement!.id)}/attachments`) as Promise<{ items: AnnouncementAttachment[] }>,
    enabled: Boolean(tenant && promptAnnouncement),
    retry: false,
  });

  async function markRead(id: string) {
    await api.account(tenant, `/notices/${encodeURIComponent(id)}/read`, { method: "POST", body: "{}" });
    await queryClient.invalidateQueries({ queryKey: ["notices", tenant] });
  }

  async function markAnnouncementRead(id: string) {
    setReadingAnnouncement(true);
    try {
      await api.announcements(tenant, `/${encodeURIComponent(id)}/read`, { method: "POST", body: "{}" });
      setDismissedAnnouncements((current) => new Set(current).add(`${userId}:${id}`));
      await queryClient.invalidateQueries({ queryKey: ["announcements", tenant, userId] });
    } catch {
      void toast.error(t("announcements.failed"));
    } finally {
      setReadingAnnouncement(false);
    }
  }

  function dismissAnnouncement(id: string) {
    setDismissedAnnouncements((current) => new Set(current).add(`${userId}:${id}`));
  }

  return (
    <>
    <TopbarPopover
      label={unread > 0 ? t("notices.unread", { count: unread }) : t("shell.notifications")}
      buttonClassName="icon-button"
      panelClassName="notice-menu"
      panel={(close) => (
        <>
          <div className="notice-menu-head">
            <strong>{t("notices.title")}</strong>
            {unread > 0 ? <span>{t("notices.unread", { count: unread })}</span> : null}
          </div>
          {items.length === 0 ? <p className="notice-menu-empty">{t("notices.empty")}</p> : (
            <ul className="notice-menu-list">
              {items.slice(0, 5).map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    role="menuitem"
                    className={item.read_at ? "is-read" : "is-unread"}
                    onClick={() => {
                      if (!item.read_at) void markRead(item.id);
                    }}
                  >
                    <span>{noticeTitle(item, t, locale === "en" ? "en" : "zh-CN")}</span>
                    <small>{formatDateTime(item.created_at)}</small>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {announcements.length > 0 ? <section className="notice-announcements" aria-labelledby="notice-announcements-title">
            <div className="notice-menu-head"><strong id="notice-announcements-title">{t("announcements.title")}</strong><span>{t("announcements.unreadCount", { count: unreadAnnouncements.length })}</span></div>
            <ul className="notice-menu-list">
              {announcements.slice(0, 5).map((item) => (
                <li key={item.id}>
                  <button type="button" role="menuitem" className={item.read ? "is-read" : "is-unread"} onClick={() => { close(); navigate(`/announcements/${item.id}`); }}>
                    <span>{item.title}</span>
                    <small>{item.read ? t("announcements.read") : t("announcements.unread")}</small>
                  </button>
                </li>
              ))}
            </ul>
          </section> : null}
          <button type="button" className="notice-menu-open" onClick={() => { close(); navigate("/account?tab=notices"); }}>{t("notices.open")}</button>
        </>
      )}
    >
      <Bell size={18} />
      {unread > 0 ? <span className="notice-count">{unread > 9 ? "9+" : unread}</span> : null}
    </TopbarPopover>
    {toastContextHolder}
    <Modal
      className="announcement-prompt"
      open={Boolean(promptAnnouncement)}
      title={promptAnnouncement?.title}
      onCancel={() => { if (promptAnnouncement) dismissAnnouncement(promptAnnouncement.id); }}
      footer={[
        <Button key="later" className="announcement-prompt-dismiss" onClick={() => { if (promptAnnouncement) dismissAnnouncement(promptAnnouncement.id); }}>{t("announcements.later")}</Button>,
        <Button key="read" type="primary" loading={readingAnnouncement} onClick={() => { if (promptAnnouncement) void markAnnouncementRead(promptAnnouncement.id); }}>{t("announcements.confirmRead")}</Button>,
      ]}
      destroyOnHidden
    >
      {promptAnnouncement ? <div className="announcement-prompt-content">
        <Tag>{t(`announcements.severityValue.${promptAnnouncement.severity}` as "announcements.severityValue.info")}</Tag>
        <RichTextView html={promptAnnouncement.body_html} />
        {(promptAttachments.data?.items.length ?? 0) > 0 ? <section className="announcement-prompt-attachments" aria-label={t("announcements.attachments")}>
          <strong>{t("announcements.attachments")}</strong>
          <ul>{promptAttachments.data!.items.map((attachment) => <li key={attachment.id}>
            <a href={attachment.url}><Paperclip size={15} /><span>{attachment.file_name}</span></a>
            <a href={attachment.download_url} aria-label={t("announcements.downloadAttachment", { name: attachment.file_name })}><Download size={15} /></a>
          </li>)}</ul>
        </section> : null}
      </div> : null}
    </Modal>
    </>
  );
}
